import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5555/api';

// Create axios instance with auth token
const createAuthenticatedRequest = () => {
    const token = localStorage.getItem('eldai_token');
    return axios.create({
        baseURL: API_BASE_URL,
        headers: {
            'Authorization': token ? `Bearer ${token}` : '',
        }
    });
};

export interface CarouselImage {
    filename: string;
    url: string;
    size: number;
    lastModified: string;
    slideNumber?: string | null;
    title?: string;
    subtitle?: string;
    ctaText?: string;
    ctaLink?: string;
}

export interface CarouselUploadResponse {
    success: boolean;
    message: string;
    image?: CarouselImage;
}

export interface CarouselListResponse {
    success: boolean;
    images: CarouselImage[];
    count: number;
}

export const carouselService = {
    // Get all carousel images
    getCarouselImages: async (): Promise<CarouselListResponse> => {
        try {
            const api = createAuthenticatedRequest();
            const response = await api.get('/carousel');
            return response.data;
        } catch (error: any) {
            console.error('Erreur récupération images carousel:', error);
            throw new Error(error.response?.data?.message || 'Erreur lors de la récupération des images du carousel');
        }
    },

    // Upload new carousel image
    uploadCarouselImage: async (
        file: File,
        metadata: { title?: string; subtitle?: string; ctaText?: string; ctaLink?: string }
    ): Promise<CarouselUploadResponse> => {
        try {
            const api = createAuthenticatedRequest();
            const formData = new FormData();

            formData.append('image', file);
            if (metadata.title) formData.append('title', metadata.title);
            if (metadata.subtitle) formData.append('subtitle', metadata.subtitle);
            if (metadata.ctaText) formData.append('ctaText', metadata.ctaText);
            if (metadata.ctaLink) formData.append('ctaLink', metadata.ctaLink);

            const response = await api.post('/carousel', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            return response.data;
        } catch (error: any) {
            console.error('Erreur upload image carousel:', error);
            throw new Error(error.response?.data?.message || 'Erreur lors de l\'upload de l\'image du carousel');
        }
    },

    // Replace existing carousel image
    replaceCarouselImage: async (filename: string, file: File): Promise<CarouselUploadResponse> => {
        try {
            const api = createAuthenticatedRequest();
            const formData = new FormData();
            formData.append('image', file);

            const response = await api.put(`/carousel/${filename}`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                }
            });

            return response.data;
        } catch (error: any) {
            console.error('Erreur remplacement image carousel:', error);
            throw new Error(error.response?.data?.message || 'Erreur lors du remplacement de l\'image du carousel');
        }
    },

    // Delete carousel image
    deleteCarouselImage: async (filename: string): Promise<{ success: boolean; message: string }> => {
        try {
            const api = createAuthenticatedRequest();
            const response = await api.delete(`/carousel/${filename}`);
            return response.data;
        } catch (error: any) {
            console.error('Erreur suppression image carousel:', error);
            throw new Error(error.response?.data?.message || 'Erreur lors de la suppression de l\'image du carousel');
        }
    }
};
